import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

const API_BASE_URL = import.meta.env.FARM_API_BASE_URL;

interface AskResponse {
  node: string;
  state:
    | {
        generation: string;
      }
    | string;
}

interface StreamError {
  error: string;
}

interface StreamStatus {
  status: string;
}

export const useAskQuestion = () => {
  const [answer, setAnswer] = useState<string>('');

  const mutation = useMutation({
    mutationFn: async (question: string) => {
      const headers = new Headers({
        'Content-Type': 'application/json',
        Authorization: 'authme',
        'X-test': 'test',
      });

      const response = await fetch(`${API_BASE_URL}/api/ask/`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ text: question }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let finalAnswer = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.trim() === '') continue;

            try {
              console.log(line);
              const data = JSON.parse(line) as
                | AskResponse
                | StreamStatus
                | StreamError;

              // Ignore error objects in the stream
              if ('error' in data) {
                console.log('Ignoring stream error:', data.error);
                continue;
              }

              // Check if stream is done
              if ('status' in data && data.status === 'done') {
                break;
              }

              // Process generation nodes
              if ('node' in data && data.node === 'generation') {
                const stateContent =
                  typeof data.state === 'string'
                    ? data.state
                    : data.state.generation;
                finalAnswer = stateContent;
                setAnswer(finalAnswer);
              }
            } catch (e) {
              console.error('Error parsing JSON:', e);
            }
          }
        }
      } else {
        console.error('Response body is not readable');
      }

      return finalAnswer;
    },
  });

  const askQuestion = (question: string) => {
    setAnswer('');
    return mutation.mutateAsync(question);
  };

  return {
    askQuestion,
    answer,
    isError: mutation.isError,
    error: mutation.error,
    isLoading: mutation.isPending, // Add this line
  };
};
